/**
 * Pure math helpers shared by the animation runtime, the cine solvers, the engine host mirrors and tests
 * (architecture checklist 3: mat4 + quaternion helpers, Euler **YXZ** intrinsic, quaternion log/exp for the
 * creatures §C1.3 Hermite).
 *
 * Conventions (architecture §3.6 "Units and axes", creatures §B1):
 * - Right-handed, +Y up. Creature-local: +Z forward, +X = the creature's own left. Arena world: +X screen right,
 *   +Z toward the camera.
 * - Quaternions are `[x, y, z, w]`, unit length, Hamilton product, rotating column vectors: `v' = q v q*`.
 * - Matrices are 4×4 **column-major** Float32/64 arrays (WGSL `mat4x4f` layout): element (row r, col c) is `m[c*4 + r]`,
 *   translation in `m[12..14]`. `m4Mul(out, a, b)` = a · b (apply b first).
 * - Euler angles are authored in **degrees** per axis `(x, y, z)` and composed **YXZ intrinsic**: rotate about Y (yaw),
 *   then the new X (pitch), then the new Z (roll), i.e. `q = qY · qX · qZ`. Pitch through ±90° is no special case.
 * - Clip/space signs (§B1): upward bones X+ bend forward, Y+ turn to the creature's left, Z+ tilt toward its right
 *   shoulder; hanging limbs X− swing forward; `_L` limbs Z+ swing outward.
 *
 * Functions write into an `out` argument and return it; nothing allocates unless the name says `new`/`create`.
 * Every function is deterministic and allocation-free in the frame loop.
 */

/** Any mutable numeric array (Float32Array in hot paths, plain arrays in tests and data). */
export type NumArray = Float32Array | Float64Array | number[];
/** 3-component vector. */
export type V3 = NumArray;
/** Quaternion [x, y, z, w]. */
export type Quat = NumArray;
/** 4×4 column-major matrix (16 numbers). */
export type M4 = NumArray;

export const DEG2RAD = Math.PI / 180;
export const RAD2DEG = 180 / Math.PI;

// ── scalars ─────────────────────────────────────────────────────────────────────────────────────────────────────────

export const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);
export const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
/** Inverse lerp: where `x` sits between a and b (unclamped; 0 when a === b). */
export const invLerp = (a: number, b: number, x: number): number => (a === b ? 0 : (x - a) / (b - a));
/** Hermite smoothstep of x between edges e0 and e1. */
export function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp01(invLerp(e0, e1, x));
  return t * t * (3 - 2 * t);
}
/** |a − b| ≤ eps. */
export const approxEq = (a: number, b: number, eps = 1e-6): boolean => Math.abs(a - b) <= eps;
/** Wraps degrees into (−180, 180]. */
export function wrapDeg(d: number): number {
  let r = d % 360;
  if (r <= -180) r += 360;
  else if (r > 180) r -= 360;
  return r;
}

// ── vec3 ────────────────────────────────────────────────────────────────────────────────────────────────────────────

export function v3(x = 0, y = 0, z = 0): Float32Array { const o = new Float32Array(3); o[0] = x; o[1] = y; o[2] = z; return o; }
export function v3set<T extends V3>(out: T, x: number, y: number, z: number): T { out[0] = x; out[1] = y; out[2] = z; return out; }
export function v3copy<T extends V3>(out: T, a: V3): T { out[0] = a[0]; out[1] = a[1]; out[2] = a[2]; return out; }
export function v3add<T extends V3>(out: T, a: V3, b: V3): T { out[0] = a[0] + b[0]; out[1] = a[1] + b[1]; out[2] = a[2] + b[2]; return out; }
export function v3sub<T extends V3>(out: T, a: V3, b: V3): T { out[0] = a[0] - b[0]; out[1] = a[1] - b[1]; out[2] = a[2] - b[2]; return out; }
export function v3scale<T extends V3>(out: T, a: V3, s: number): T { out[0] = a[0] * s; out[1] = a[1] * s; out[2] = a[2] * s; return out; }
/** out = a + b·s */
export function v3addScaled<T extends V3>(out: T, a: V3, b: V3, s: number): T { out[0] = a[0] + b[0] * s; out[1] = a[1] + b[1] * s; out[2] = a[2] + b[2] * s; return out; }
export const v3dot = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export function v3cross<T extends V3>(out: T, a: V3, b: V3): T {
  const ax = a[0], ay = a[1], az = a[2], bx = b[0], by = b[1], bz = b[2];
  out[0] = ay * bz - az * by; out[1] = az * bx - ax * bz; out[2] = ax * by - ay * bx; return out;
}
export const v3len = (a: V3): number => Math.hypot(a[0], a[1], a[2]);
export const v3dist = (a: V3, b: V3): number => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
/** Normalises; a zero vector stays zero (no NaN). */
export function v3normalize<T extends V3>(out: T, a: V3): T {
  const l = v3len(a);
  const s = l > 1e-12 ? 1 / l : 0;
  out[0] = a[0] * s; out[1] = a[1] * s; out[2] = a[2] * s; return out;
}
export function v3lerp<T extends V3>(out: T, a: V3, b: V3, t: number): T {
  out[0] = a[0] + (b[0] - a[0]) * t; out[1] = a[1] + (b[1] - a[1]) * t; out[2] = a[2] + (b[2] - a[2]) * t; return out;
}

// ── quaternions [x, y, z, w] ───────────────────────────────────────────────────────────────────────────────────────

export function quat(x = 0, y = 0, z = 0, w = 1): Float32Array { const o = new Float32Array(4); o[0] = x; o[1] = y; o[2] = z; o[3] = w; return o; }
export function qIdentity<T extends Quat>(out: T): T { out[0] = 0; out[1] = 0; out[2] = 0; out[3] = 1; return out; }
export function qCopy<T extends Quat>(out: T, a: Quat): T { out[0] = a[0]; out[1] = a[1]; out[2] = a[2]; out[3] = a[3]; return out; }
export const qDot = (a: Quat, b: Quat): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3];
/** Hamilton product out = a · b (apply b first, then a). */
export function qMul<T extends Quat>(out: T, a: Quat, b: Quat): T {
  const ax = a[0], ay = a[1], az = a[2], aw = a[3], bx = b[0], by = b[1], bz = b[2], bw = b[3];
  out[0] = aw * bx + ax * bw + ay * bz - az * by;
  out[1] = aw * by - ax * bz + ay * bw + az * bx;
  out[2] = aw * bz + ax * by - ay * bx + az * bw;
  out[3] = aw * bw - ax * bx - ay * by - az * bz;
  return out;
}
export function qConjugate<T extends Quat>(out: T, a: Quat): T { out[0] = -a[0]; out[1] = -a[1]; out[2] = -a[2]; out[3] = a[3]; return out; }
export function qNormalize<T extends Quat>(out: T, a: Quat): T {
  const l = Math.hypot(a[0], a[1], a[2], a[3]);
  if (l < 1e-12) return qIdentity(out);
  const s = 1 / l;
  out[0] = a[0] * s; out[1] = a[1] * s; out[2] = a[2] * s; out[3] = a[3] * s; return out;
}
/** Rotation of `rad` radians about a unit `axis`. */
export function qFromAxisAngle<T extends Quat>(out: T, axis: V3, rad: number): T {
  const h = rad * 0.5, s = Math.sin(h);
  out[0] = axis[0] * s; out[1] = axis[1] * s; out[2] = axis[2] * s; out[3] = Math.cos(h); return out;
}
/**
 * Euler YXZ intrinsic, degrees per axis: `q = qY(yDeg) · qX(xDeg) · qZ(zDeg)` (creatures §B1). This is the conversion
 * clip keys go through once at load (`BoneKey.r` = [x, y, z] degrees).
 */
export function qFromEulerYXZ<T extends Quat>(out: T, xDeg: number, yDeg: number, zDeg: number): T {
  const hx = xDeg * DEG2RAD * 0.5, hy = yDeg * DEG2RAD * 0.5, hz = zDeg * DEG2RAD * 0.5;
  const sx = Math.sin(hx), cx = Math.cos(hx), sy = Math.sin(hy), cy = Math.cos(hy), sz = Math.sin(hz), cz = Math.cos(hz);
  // qY·qX = (cy·sx, sy·cx, −sy·sx, cy·cx); then ·qZ
  const ax = cy * sx, ay = sy * cx, az = -sy * sx, aw = cy * cx;
  out[0] = ax * cz + ay * sz;
  out[1] = ay * cz - ax * sz;
  out[2] = aw * sz + az * cz;
  out[3] = aw * cz - az * sz;
  return out;
}
/** Inverse of {@link qFromEulerYXZ}: writes [xDeg, yDeg, zDeg] (x ∈ [−90, 90]). */
export function qToEulerYXZ<T extends V3>(out: T, q: Quat): T {
  const x = q[0], y = q[1], z = q[2], w = q[3];
  // rotation matrix entries (row, col) of R = Ry·Rx·Rz
  const m12 = 2 * (y * z - w * x);          // = −sin(pitch)
  const m02 = 2 * (x * z + w * y);
  const m22 = 1 - 2 * (x * x + y * y);
  const m10 = 2 * (x * y + w * z);
  const m11 = 1 - 2 * (x * x + z * z);
  const sp = clamp(-m12, -1, 1);
  const pitch = Math.asin(sp);
  let yaw: number, roll: number;
  if (Math.abs(sp) < 0.9999999) {
    yaw = Math.atan2(m02, m22);
    roll = Math.atan2(m10, m11);
  } else {
    // gimbal: fold roll into yaw
    const m00 = 1 - 2 * (y * y + z * z), m20 = 2 * (x * z - w * y);
    yaw = Math.atan2(-m20, m00);
    roll = 0;
  }
  out[0] = pitch * RAD2DEG; out[1] = yaw * RAD2DEG; out[2] = roll * RAD2DEG;
  return out;
}
/** Rotates vector v by q: out = q v q*. */
export function qRotateV3<T extends V3>(out: T, q: Quat, v: V3): T {
  const qx = q[0], qy = q[1], qz = q[2], qw = q[3], vx = v[0], vy = v[1], vz = v[2];
  // t = 2 · cross(q.xyz, v); v' = v + w·t + cross(q.xyz, t)
  const tx = 2 * (qy * vz - qz * vy), ty = 2 * (qz * vx - qx * vz), tz = 2 * (qx * vy - qy * vx);
  out[0] = vx + qw * tx + (qy * tz - qz * ty);
  out[1] = vy + qw * ty + (qz * tx - qx * tz);
  out[2] = vz + qw * tz + (qx * ty - qy * tx);
  return out;
}
/** Shortest-path spherical interpolation (falls back to nlerp for nearly equal rotations). */
export function qSlerp<T extends Quat>(out: T, a: Quat, b: Quat, t: number): T {
  let bx = b[0], by = b[1], bz = b[2], bw = b[3];
  let cos = a[0] * bx + a[1] * by + a[2] * bz + a[3] * bw;
  if (cos < 0) { cos = -cos; bx = -bx; by = -by; bz = -bz; bw = -bw; }
  let k0: number, k1: number;
  if (cos > 0.9995) { k0 = 1 - t; k1 = t; } else {
    const th = Math.acos(cos), s = Math.sin(th);
    k0 = Math.sin((1 - t) * th) / s; k1 = Math.sin(t * th) / s;
  }
  out[0] = a[0] * k0 + bx * k1; out[1] = a[1] * k0 + by * k1; out[2] = a[2] * k0 + bz * k1; out[3] = a[3] * k0 + bw * k1;
  return qNormalize(out, out);
}
/** Normalised lerp along the shortest path. */
export function qNlerp<T extends Quat>(out: T, a: Quat, b: Quat, t: number): T {
  const sgn = qDot(a, b) < 0 ? -1 : 1;
  out[0] = a[0] + (b[0] * sgn - a[0]) * t; out[1] = a[1] + (b[1] * sgn - a[1]) * t;
  out[2] = a[2] + (b[2] * sgn - a[2]) * t; out[3] = a[3] + (b[3] * sgn - a[3]) * t;
  return qNormalize(out, out);
}
/** Logarithm of a unit quaternion: the vector part `θ/2 · axis` (pure quaternion, w omitted). */
export function qLog<T extends V3>(out: T, q: Quat): T {
  const vx = q[0], vy = q[1], vz = q[2];
  const vl = Math.hypot(vx, vy, vz);
  const w = clamp(q[3], -1, 1);
  if (vl < 1e-12) { out[0] = 0; out[1] = 0; out[2] = 0; return out; }
  const half = Math.atan2(vl, w);
  const s = half / vl;
  out[0] = vx * s; out[1] = vy * s; out[2] = vz * s; return out;
}
/** Exponential of a pure quaternion `v` (= θ/2 · axis) → unit quaternion. Inverse of {@link qLog}. */
export function qExp<T extends Quat>(out: T, v: V3): T {
  const half = Math.hypot(v[0], v[1], v[2]);
  if (half < 1e-12) return qIdentity(out);
  const s = Math.sin(half) / half;
  out[0] = v[0] * s; out[1] = v[1] * s; out[2] = v[2] * s; out[3] = Math.cos(half); return out;
}
/** Angle between two unit rotations, radians (0..π). */
export function qAngle(a: Quat, b: Quat): number {
  return 2 * Math.acos(clamp(Math.abs(qDot(a, b)), 0, 1));
}
/** L↔R mirror of a local rotation (creatures §B1 authoring utility): (x, y, z, w) → (x, −y, −z, w). */
export function qMirrorLR<T extends Quat>(out: T, q: Quat): T { out[0] = q[0]; out[1] = -q[1]; out[2] = -q[2]; out[3] = q[3]; return out; }

// ── mat4, column-major ────────────────────────────────────────────────────────────────────────────────────────────

export function m4(): Float32Array { return m4Identity(new Float32Array(16)); }
export function m4Identity<T extends M4>(out: T): T {
  for (let i = 0; i < 16; i++) out[i] = i % 5 === 0 ? 1 : 0;
  return out;
}
export function m4Copy<T extends M4>(out: T, a: M4): T { for (let i = 0; i < 16; i++) out[i] = a[i]; return out; }
/** out = a · b (column-major; transforms by b first). Safe when out aliases a or b. */
export function m4Mul<T extends M4>(out: T, a: M4, b: M4): T {
  const a00 = a[0], a01 = a[1], a02 = a[2], a03 = a[3], a10 = a[4], a11 = a[5], a12 = a[6], a13 = a[7];
  const a20 = a[8], a21 = a[9], a22 = a[10], a23 = a[11], a30 = a[12], a31 = a[13], a32 = a[14], a33 = a[15];
  for (let c = 0; c < 4; c++) {
    const b0 = b[c * 4], b1 = b[c * 4 + 1], b2 = b[c * 4 + 2], b3 = b[c * 4 + 3];
    out[c * 4] = a00 * b0 + a10 * b1 + a20 * b2 + a30 * b3;
    out[c * 4 + 1] = a01 * b0 + a11 * b1 + a21 * b2 + a31 * b3;
    out[c * 4 + 2] = a02 * b0 + a12 * b1 + a22 * b2 + a32 * b3;
    out[c * 4 + 3] = a03 * b0 + a13 * b1 + a23 * b2 + a33 * b3;
  }
  return out;
}
/** T · R · S from a quaternion, a translation and a per-axis scale. */
export function m4FromRTS<T extends M4>(out: T, q: Quat, t: V3, s: V3): T {
  const x = q[0], y = q[1], z = q[2], w = q[3];
  const x2 = x + x, y2 = y + y, z2 = z + z;
  const xx = x * x2, xy = x * y2, xz = x * z2, yy = y * y2, yz = y * z2, zz = z * z2, wx = w * x2, wy = w * y2, wz = w * z2;
  const sx = s[0], sy = s[1], sz = s[2];
  out[0] = (1 - (yy + zz)) * sx; out[1] = (xy + wz) * sx; out[2] = (xz - wy) * sx; out[3] = 0;
  out[4] = (xy - wz) * sy; out[5] = (1 - (xx + zz)) * sy; out[6] = (yz + wx) * sy; out[7] = 0;
  out[8] = (xz + wy) * sz; out[9] = (yz - wx) * sz; out[10] = (1 - (xx + yy)) * sz; out[11] = 0;
  out[12] = t[0]; out[13] = t[1]; out[14] = t[2]; out[15] = 1;
  return out;
}
export function m4Translation<T extends M4>(out: T, x: number, y: number, z: number): T {
  m4Identity(out); out[12] = x; out[13] = y; out[14] = z; return out;
}
export function m4Scaling<T extends M4>(out: T, x: number, y: number, z: number): T {
  m4Identity(out); out[0] = x; out[5] = y; out[10] = z; return out;
}
/** Rotation about +Y by `rad` (yaw; positive turns +Z toward +X). */
export function m4RotationY<T extends M4>(out: T, rad: number): T {
  const c = Math.cos(rad), s = Math.sin(rad);
  m4Identity(out); out[0] = c; out[2] = -s; out[8] = s; out[10] = c; return out;
}
export function m4Transpose<T extends M4>(out: T, a: M4): T {
  if (out === a) {
    let t: number;
    t = a[1]; out[1] = a[4]; out[4] = t; t = a[2]; out[2] = a[8]; out[8] = t; t = a[3]; out[3] = a[12]; out[12] = t;
    t = a[6]; out[6] = a[9]; out[9] = t; t = a[7]; out[7] = a[13]; out[13] = t; t = a[11]; out[11] = a[14]; out[14] = t;
    return out;
  }
  for (let r = 0; r < 4; r++) for (let c = 0; c < 4; c++) out[c * 4 + r] = a[r * 4 + c];
  return out;
}
export function m4Determinant(a: M4): number {
  const b00 = a[0] * a[5] - a[1] * a[4], b01 = a[0] * a[6] - a[2] * a[4], b02 = a[0] * a[7] - a[3] * a[4];
  const b03 = a[1] * a[6] - a[2] * a[5], b04 = a[1] * a[7] - a[3] * a[5], b05 = a[2] * a[7] - a[3] * a[6];
  const b06 = a[8] * a[13] - a[9] * a[12], b07 = a[8] * a[14] - a[10] * a[12], b08 = a[8] * a[15] - a[11] * a[12];
  const b09 = a[9] * a[14] - a[10] * a[13], b10 = a[9] * a[15] - a[11] * a[13], b11 = a[10] * a[15] - a[11] * a[14];
  return b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
}
/** General inverse; returns null (and leaves out untouched) when singular. */
export function m4Invert<T extends M4>(out: T, a: M4): T | null {
  const a00 = a[0], a01 = a[1], a02 = a[2], a03 = a[3], a10 = a[4], a11 = a[5], a12 = a[6], a13 = a[7];
  const a20 = a[8], a21 = a[9], a22 = a[10], a23 = a[11], a30 = a[12], a31 = a[13], a32 = a[14], a33 = a[15];
  const b00 = a00 * a11 - a01 * a10, b01 = a00 * a12 - a02 * a10, b02 = a00 * a13 - a03 * a10;
  const b03 = a01 * a12 - a02 * a11, b04 = a01 * a13 - a03 * a11, b05 = a02 * a13 - a03 * a12;
  const b06 = a20 * a31 - a21 * a30, b07 = a20 * a32 - a22 * a30, b08 = a20 * a33 - a23 * a30;
  const b09 = a21 * a32 - a22 * a31, b10 = a21 * a33 - a23 * a31, b11 = a22 * a33 - a23 * a32;
  const det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
  if (Math.abs(det) < 1e-20) return null;
  const id = 1 / det;
  out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * id;
  out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * id;
  out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * id;
  out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * id;
  out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * id;
  out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * id;
  out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * id;
  out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * id;
  out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * id;
  out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * id;
  out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * id;
  out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * id;
  out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * id;
  out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * id;
  out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * id;
  out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * id;
  return out;
}
/** out = m · (p, 1), with perspective divide when w ≠ 1. */
export function m4TransformPoint<T extends V3>(out: T, m: M4, p: V3): T {
  const x = p[0], y = p[1], z = p[2];
  const w = m[3] * x + m[7] * y + m[11] * z + m[15];
  const iw = w !== 0 && w !== 1 ? 1 / w : 1;
  out[0] = (m[0] * x + m[4] * y + m[8] * z + m[12]) * iw;
  out[1] = (m[1] * x + m[5] * y + m[9] * z + m[13]) * iw;
  out[2] = (m[2] * x + m[6] * y + m[10] * z + m[14]) * iw;
  return out;
}
/** out = upper-left 3×3 of m · d (directions; no translation). */
export function m4TransformDir<T extends V3>(out: T, m: M4, d: V3): T {
  const x = d[0], y = d[1], z = d[2];
  out[0] = m[0] * x + m[4] * y + m[8] * z;
  out[1] = m[1] * x + m[5] * y + m[9] * z;
  out[2] = m[2] * x + m[6] * y + m[10] * z;
  return out;
}
/** Right-handed view matrix looking from `eye` at `target` (camera looks down its −Z). */
export function m4LookAt<T extends M4>(out: T, eye: V3, target: V3, up: V3): T {
  let fx = eye[0] - target[0], fy = eye[1] - target[1], fz = eye[2] - target[2];   // camera +Z (backwards)
  let l = Math.hypot(fx, fy, fz) || 1; fx /= l; fy /= l; fz /= l;
  let rx = up[1] * fz - up[2] * fy, ry = up[2] * fx - up[0] * fz, rz = up[0] * fy - up[1] * fx;
  l = Math.hypot(rx, ry, rz) || 1; rx /= l; ry /= l; rz /= l;
  const ux = fy * rz - fz * ry, uy = fz * rx - fx * rz, uz = fx * ry - fy * rx;
  out[0] = rx; out[1] = ux; out[2] = fx; out[3] = 0;
  out[4] = ry; out[5] = uy; out[6] = fy; out[7] = 0;
  out[8] = rz; out[9] = uz; out[10] = fz; out[11] = 0;
  out[12] = -(rx * eye[0] + ry * eye[1] + rz * eye[2]);
  out[13] = -(ux * eye[0] + uy * eye[1] + uz * eye[2]);
  out[14] = -(fx * eye[0] + fy * eye[1] + fz * eye[2]);
  out[15] = 1;
  return out;
}
/** WebGPU perspective projection (clip z ∈ [0, 1]), right-handed view space. */
export function m4Perspective<T extends M4>(out: T, fovYRad: number, aspect: number, near: number, far: number): T {
  const f = 1 / Math.tan(fovYRad / 2), nf = 1 / (near - far);
  for (let i = 0; i < 16; i++) out[i] = 0;
  out[0] = f / aspect; out[5] = f; out[10] = far * nf; out[11] = -1; out[14] = far * near * nf;
  return out;
}
/** WebGPU orthographic projection (clip z ∈ [0, 1]), right-handed view space. */
export function m4Ortho<T extends M4>(out: T, left: number, right: number, bottom: number, top: number, near: number, far: number): T {
  for (let i = 0; i < 16; i++) out[i] = 0;
  const lr = 1 / (left - right), bt = 1 / (bottom - top), nf = 1 / (near - far);
  out[0] = -2 * lr; out[5] = -2 * bt; out[10] = nf; out[15] = 1;
  out[12] = (left + right) * lr; out[13] = (top + bottom) * bt; out[14] = near * nf;
  return out;
}
